import { useEffect } from 'react';

interface SEOHelmetProps {
  title: string;
  description: string;
  keywords?: string;
  canonicalUrl?: string;
  ogType?: string;
  ogImage?: string;
  pageType?: 'website' | 'article' | 'webpage';
  articleData?: {
    publishedTime?: string;
    modifiedTime?: string;
    author?: string;
  };
  faqData?: Array<{ question: string; answer: string }>;
}

export default function SEOHelmet({ title, description, keywords, canonicalUrl, ogType = 'website', ogImage, pageType = 'webpage', articleData, faqData }: SEOHelmetProps) {
  useEffect(() => {
    document.title = title;

    const setMeta = (name: string, content: string, property = false) => {
      const attr = property ? 'property' : 'name';
      let el = document.querySelector(`meta[${attr}="${name}"]`);
      if (!el) {
        el = document.createElement('meta');
        el.setAttribute(attr, name);
        document.head.appendChild(el);
      }
      el.setAttribute('content', content);
    };

    setMeta('description', description);
    if (keywords) setMeta('keywords', keywords);

    const canonical = canonicalUrl || window.location.href;
    let linkEl = document.querySelector('link[rel="canonical"]');
    if (!linkEl) {
      linkEl = document.createElement('link');
      linkEl.setAttribute('rel', 'canonical');
      document.head.appendChild(linkEl);
    }
    linkEl.setAttribute('href', canonical);

    setMeta('og:title', title, true);
    setMeta('og:description', description, true);
    setMeta('og:type', ogType, true);
    setMeta('og:url', canonical, true);
    if (ogImage) setMeta('og:image', ogImage, true);

    setMeta('twitter:card', 'summary_large_image');
    setMeta('twitter:title', title);
    setMeta('twitter:description', description);
    if (ogImage) setMeta('twitter:image', ogImage);

    const existingScripts = document.querySelectorAll('script[type="application/ld+json"]');
    existingScripts.forEach((s) => s.remove());

    const schemas: Record<string, unknown>[] = [];

    schemas.push({
      '@context': 'https://schema.org',
      '@type': 'Organization',
      name: '羞羞漫画',
      url: canonical,
      description: '国内领先的正版成人漫画在线阅读平台',
      contactPoint: {
        '@type': 'ContactPoint',
        email: 'service@xiuxiumanhua.com',
        contactType: 'customer service',
        availableLanguage: ['Chinese'],
      },
    });

    schemas.push({
      '@context': 'https://schema.org',
      '@type': 'WebSite',
      name: '羞羞漫画',
      url: canonical,
      potentialAction: {
        '@type': 'SearchAction',
        target: `${canonical}?q={search_term_string}`,
        'query-input': 'required name=search_term_string',
      },
    });

    schemas.push({
      '@context': 'https://schema.org',
      '@type': 'WebPage',
      name: title,
      description: description,
      url: canonical,
      isPartOf: {
        '@type': 'WebSite',
        name: '羞羞漫画',
        url: canonical.split('/').slice(0, 3).join('/'),
      },
    });

    if (pageType === 'article' && articleData) {
      schemas.push({
        '@context': 'https://schema.org',
        '@type': 'Article',
        headline: title,
        description: description,
        author: {
          '@type': 'Person',
          name: articleData.author || '羞羞漫画',
        },
        datePublished: articleData.publishedTime,
        dateModified: articleData.modifiedTime || articleData.publishedTime,
        publisher: {
          '@type': 'Organization',
          name: '羞羞漫画',
        },
      });
    }

    if (faqData && faqData.length > 0) {
      schemas.push({
        '@context': 'https://schema.org',
        '@type': 'FAQPage',
        mainEntity: faqData.map((item) => ({
          '@type': 'Question',
          name: item.question,
          acceptedAnswer: {
            '@type': 'Answer',
            text: item.answer,
          },
        })),
      });
    }

    schemas.forEach((schema) => {
      const script = document.createElement('script');
      script.type = 'application/ld+json';
      script.textContent = JSON.stringify(schema);
      document.head.appendChild(script);
    });

    return () => {
      existingScripts.forEach((s) => s.remove());
      schemas.forEach(() => {
        const scripts = document.querySelectorAll('script[type="application/ld+json"]');
        scripts.forEach((s) => s.remove());
      });
    };
  }, [title, description, keywords, canonicalUrl, ogType, ogImage, pageType, articleData, faqData]);

  return null;
}